import { ComponentType } from 'react';
import { JSONObject } from '../../../../types/types';
import { useVisibilityResolve } from '../../../../hooks/useVisibilityResolve';
import { useFormInitRegistration } from '../hooks/useFormInitRegistration';

// DatasetForm-specific HOC: resolves `visible` expression AND registers with
// FormInitProvider so the loader waits for this component to finish initializing.
// Use instead of withVisiblityResolve inside DatasetForm — keeps the base HOC
// independent and reusable outside forms.
export type WithFormVisibilityInitProp = {
  visible?: boolean | string;
  resolveData: JSONObject;
};

export const withFormVisibilityInit = <P extends WithFormVisibilityInitProp>(
  Component: ComponentType<Omit<P, 'visible'> & { visible: boolean }>
) => {
  const WithFormVisibilityInit = (props: P) => {
    const { visible, resolveData, ...rest } = props;
    // Identity for the `visibility` debug feature — form items carry name,
    // form groups only caption.
    const { name, caption } = props as { name?: string; caption?: string };
    const { visible: resolvedVisible, pending } = useVisibilityResolve(
      visible,
      resolveData,
      name ?? caption
    );
    useFormInitRegistration(pending);

    return (
      <Component
        {...(rest as Omit<P, 'visible'>)}
        visible={resolvedVisible}
        resolveData={resolveData}
      />
    );
  };

  WithFormVisibilityInit.displayName = `withFormVisibilityInit(${
    Component.displayName || Component.name || 'Component'
  })`;

  return WithFormVisibilityInit;
};
